/**
 * ParlaysStatsGrid Component
 * Animated summary dashboard for tracked parlays
 * Inspired by Aceternity UI bento-grid pattern with framer-motion animations
 */

import { AnimatedNumber } from '@components/ui/AnimatedNumber';
import { GlowStatCard } from '@components/ui/GlowStatCard';
import type { ParlayStats, TrackedParlay } from '@hooks/useTrackedParlays';
import { hitRatePct } from '@parlay-picker/shared/parlayStats';
import { cn } from '@utils/cn';
import { AnimatePresence, motion } from 'framer-motion';
import type { ReactElement } from 'react';
import { calculateStreak } from './utils';

// =============================================================================
// Types
// =============================================================================

interface ParlaysStatsGridProps {
  stats: ParlayStats;
  trackedParlays: TrackedParlay[];
  liveGamesCount: number;
  isConnected: boolean;
  /** Games the SSE hook has to subscribe to. Zero means there is nothing to connect to. */
  trackedGamesCount: number;
}

// =============================================================================
// Component
// =============================================================================

export function ParlaysStatsGrid({
  stats,
  trackedParlays,
  liveGamesCount,
  isConnected,
  trackedGamesCount,
}: ParlaysStatsGridProps): ReactElement {
  // Coalesce to 0: the stats endpoint can return null for these before its own
  // fix deploys, and null must never render as "nullW - nullL".
  const won = stats.won ?? 0;
  const lost = stats.lost ?? 0;
  const pending = stats.pending ?? 0;
  const voided = stats.voided ?? 0;
  // Decided = won + lost only; push/void/pending are refunds or undecided and must
  // not sit in the denominator. null until something is decided (renders a dash).
  const hitRate = hitRatePct(won, lost);
  const hitRateForAccent = hitRate ?? 0;

  const streak = calculateStreak(trackedParlays);

  const hitRateAccent =
    hitRateForAccent >= 30
      ? 'success'
      : hitRateForAccent >= 20
        ? 'default'
        : hitRateForAccent > 0
          ? 'danger'
          : 'default';
  const streakAccent = streak.type === 'W' ? 'success' : streak.type === 'L' ? 'danger' : 'default';

  return (
    <div data-testid="parlays-stats-grid" className="grid grid-cols-2 @3xl:grid-cols-4 gap-3 mb-6">
      {/* Total Tracked */}
      <GlowStatCard
        label="Total Tracked"
        sub={pending > 0 ? `${pending} pending` : 'All settled'}
        accent="default"
      >
        <AnimatedNumber value={stats.total} />
      </GlowStatCard>

      {/* Win Rate */}
      <GlowStatCard
        label="Win Rate"
        sub={`${won}W - ${lost}L${voided > 0 ? ` - ${voided}V` : ''}`}
        accent={hitRateAccent}
      >
        <span
          className={cn(
            hitRateForAccent >= 30
              ? 'text-theme-success'
              : hitRateForAccent >= 20
                ? 'text-theme-text'
                : hitRateForAccent > 0
                  ? 'text-theme-danger'
                  : '',
          )}
        >
          {hitRate != null ? <AnimatedNumber value={hitRate} suffix="%" /> : '-'}
        </span>
      </GlowStatCard>

      {/* Current Streak */}
      <GlowStatCard
        label="Current Streak"
        sub={streak.count > 0 ? `Last ${streak.count} settled` : 'No settled parlays'}
        accent={streakAccent}
      >
        <span
          className={cn(
            streak.type === 'W'
              ? 'text-theme-success'
              : streak.type === 'L'
                ? 'text-theme-danger'
                : '',
          )}
        >
          {streak.count > 0 ? `${streak.count}${streak.type}` : '-'}
        </span>
      </GlowStatCard>

      {/* Live Games */}
      <GlowStatCard
        label="Live Games"
        sub={
          // The hook only opens a connection when it has games to track, so without
          // this a slate with nothing live sat on "Connecting..." forever.
          isConnected
            ? 'SSE connected'
            : trackedGamesCount === 0
              ? 'No live games'
              : 'Connecting...'
        }
        accent={liveGamesCount > 0 ? 'live' : 'default'}
      >
        <div className="flex items-center gap-2">
          <AnimatePresence>
            {liveGamesCount > 0 && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                className="inline-block w-3 h-3 bg-theme-danger rounded-full animate-pulse"
              />
            )}
          </AnimatePresence>
          <span
            className={cn(liveGamesCount > 0 ? 'text-theme-danger' : 'text-theme-text-secondary')}
          >
            {liveGamesCount > 0 ? liveGamesCount : 'None'}
          </span>
        </div>
      </GlowStatCard>
    </div>
  );
}
